import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  CircularProgress,
} from "@mui/material";
import {
  Dashboard as DashboardIcon,
  CalendarMonth as CalendarIcon,
  Chat as ChatIcon,
  CreditCard as CreditCardIcon,
  BarChart as ResultIcon,
  Assignment as AssignmentIcon,
  Campaign as CampaignIcon,
  MenuBook as MenuBookIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import BackgoundGraphic from '../image/BackgroundGraphic.png';

const ParentSidebar = ({ availablePages }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(!availablePages || availablePages.length === 0);

  useEffect(() => {
    if (availablePages && availablePages.length > 0) {
      setLoading(false);
    }
  }, [availablePages]);

  // Normalize URL for comparison
  const normalizeUrl = (url) => {
    return url.replace(/^(\.\.\/)*/, '').replace(/^\//, '');
  };

  // Check if current page is active
  const isActive = (url) => {
    const normalizedUrl = normalizeUrl(url);
    const currentPath = location.pathname.replace(/^\//, '');
    return currentPath === normalizedUrl;
  };

  // Comprehensive icon mapping
  const getIconForForm = (formName) => {
    const formNameLower = formName.toLowerCase();
    
    if (formNameLower.includes('result')) return <ResultIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('fee') || formNameLower.includes('payment')) return <CreditCardIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('time') || formNameLower.includes('schedule')) return <CalendarIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('topic') || formNameLower.includes('tracker')) return <MenuBookIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('announcement') || formNameLower.includes('notice')) return <CampaignIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('chat') || formNameLower.includes('message')) return <ChatIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('assignment')) return <AssignmentIcon sx={{ fontSize: 24 }} />;
    if (formNameLower.includes('homework')) return <MenuBookIcon sx={{ fontSize: 24 }} />;
    
    return <InfoIcon sx={{ fontSize: 24 }} />;
  };

  const handleNavigation = (url) => {
    const normalizedUrl = normalizeUrl(url);
    navigate(`/${normalizedUrl}`);
  };

  const isDashboardActive = location.pathname === '/ParentDashboard';

  return (
    <Box sx={{ marginTop: "70px" }}>
      <Drawer
        variant="permanent"
        sx={{
          width: 280,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: 280,
            boxSizing: "border-box",
            background: `url(${BackgoundGraphic}) no-repeat center center`,
            backgroundSize: "165%",
            backgroundPosition: "left calc(100% - 50px)",
            marginTop: "70px",
            border: "none",
            borderRight: "1px solid rgba(75,108,183,0.12)",
            boxShadow: "4px 0 16px rgba(75,108,183,0.08)",
          },
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: '200px' }}>
            <CircularProgress />
          </Box>
        ) : (
          <List sx={{ p: 2.5, mt: 1 }}>
            {/* Dashboard - Always visible and fixed */}
            <ListItem disablePadding sx={{ mb: 1 }}>
              <ListItemButton
                selected={isDashboardActive}
                onClick={() => navigate('/ParentDashboard')}
                sx={{
                  borderRadius: "12px",
                  transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                  px: 2.5,
                  py: 1.5,
                  minHeight: "52px",
                  "&:hover": {
                    backgroundColor: "rgba(75,108,183,0.06)",
                    transform: "translateX(4px)",
                    boxShadow: "0 2px 8px rgba(75,108,183,0.12)",
                  },
                  "&.Mui-selected": {
                    background: "linear-gradient(135deg, rgba(75,108,183,0.15) 0%, rgba(24,40,72,0.12) 100%)",
                    borderLeft: "4px solid rgba(24,40,72,1)",
                    paddingLeft: "16px",
                    boxShadow: "0 4px 12px rgba(75,108,183,0.2)",
                    "&:hover": {
                      background: "linear-gradient(135deg, rgba(75,108,183,0.18) 0%, rgba(24,40,72,0.15) 100%)",
                    },
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    minWidth: 44, 
                    color: isDashboardActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,0.8)",
                  }}
                >
                  <DashboardIcon sx={{ fontSize: 24 }} />
                </ListItemIcon>
                <ListItemText
                  primary="Dashboard"
                  sx={{
                    "& .MuiTypography-root": {
                      fontWeight: isDashboardActive ? 700 : 600,
                      fontSize: "0.95rem",
                      letterSpacing: "0.3px",
                      color: isDashboardActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,1)",
                    }
                  }}
                />
              </ListItemButton>
            </ListItem>

            {/* Dynamic menu items from API */}
            {availablePages && availablePages.map((item) => {
              const itemActive = isActive(item.formUrl);
              
              return (
                <ListItem key={item.formId} disablePadding sx={{ mb: 1 }}>
                  <ListItemButton
                    selected={itemActive}
                    onClick={() => handleNavigation(item.formUrl)}
                    sx={{
                      borderRadius: "12px",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                      px: 2.5,
                      py: 1.5,
                      minHeight: "52px",
                      "&:hover": {
                        backgroundColor: "rgba(75,108,183,0.06)",
                        transform: "translateX(4px)",
                        boxShadow: "0 2px 8px rgba(75,108,183,0.12)",
                      },
                      "&.Mui-selected": {
                        background: "linear-gradient(135deg, rgba(75,108,183,0.15) 0%, rgba(24,40,72,0.12) 100%)",
                        borderLeft: "4px solid rgba(24,40,72,1)",
                        paddingLeft: "16px",
                        boxShadow: "0 4px 12px rgba(75,108,183,0.2)",
                        "&:hover": {
                          background: "linear-gradient(135deg, rgba(75,108,183,0.18) 0%, rgba(24,40,72,0.15) 100%)",
                        },
                      },
                    }}
                  >
                    <ListItemIcon 
                      sx={{ 
                        minWidth: 44, 
                        color: itemActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,0.8)",
                      }}
                    >
                      {getIconForForm(item.formName)}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.formName}
                      sx={{
                        "& .MuiTypography-root": {
                          fontWeight: itemActive ? 700 : 600,
                          fontSize: "0.95rem",
                          letterSpacing: "0.3px",
                          color: itemActive ? "rgba(24,40,72,1)" : "rgba(75,108,183,1)",
                        }
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </Drawer>
    </Box>
  );
};

export default ParentSidebar;